import { TypedDocumentNode, gql } from '@apollo/client';

import {
  CreditCardType,
  StarkwareSignatureInput,
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';

import { useGetAuthorizationApprovals } from 'hooks/useGetAuthorizationApprovals';

import {
  PrepareAcceptOfferMutation,
  PrepareAcceptOfferMutationVariables,
} from './__generated__/usePrepareAcceptOffer.graphql';

const PREPARE_ACCEPT_OFFER_MUTATION = gql`
  mutation PrepareAcceptOfferMutation($input: prepareAcceptOfferInput!) {
    prepareAcceptOffer(input: $input) {
      authorizations {
        id
        ...useGetAuthorizationApprovals_authorizationRequest
      }
      errors {
        message
        code
      }
    }
  }
  ${useGetAuthorizationApprovals.fragments.authorizationRequest}
` as TypedDocumentNode<
  PrepareAcceptOfferMutation,
  PrepareAcceptOfferMutationVariables
>;

type PrepareAcceptOfferArgs = {
  offerId: string;
  supportedCurrency: SupportedCurrency;
  tokenPaymentMethod: TokenPaymentMethod;
  conversionCreditId?: string;
  savePaymentMethod?: boolean;
  walletChallengeSignature?: StarkwareSignatureInput | null;
  attemptReference: string | null;
  signedAmount?: string;
  cardType?: CreditCardType;
  returnUrl?: string;
};
type Props = {
  signAuthorizations: boolean;
};

const usePrepareAcceptOffer = ({ signAuthorizations }: Props) => {
  const getAuthorizationApprovals = useGetAuthorizationApprovals();

  const [prepareAcceptOfferMutation, { loading }] = useMutation(
    PREPARE_ACCEPT_OFFER_MUTATION,
    { showErrorsWithSnackNotification: true }
  );

  const { exchangeRate } = useConfigContext();

  const prepareAcceptOffer = async ({
    offerId,
    supportedCurrency,
    tokenPaymentMethod,
    conversionCreditId,
    savePaymentMethod,
    walletChallengeSignature,
    attemptReference,
    signedAmount,
    cardType,
    returnUrl,
  }: PrepareAcceptOfferArgs) => {
    const settlementInfo = {
      currency: supportedCurrency,
      exchangeRateId: idFromObject(exchangeRate.id),
      paymentMethod: tokenPaymentMethod,
      ...(conversionCreditId && { conversionCreditId }),
      savePaymentMethod,
      walletChallengeSignature,
      creditCardType: cardType,
      ...(returnUrl && { returnUrl }),
    };

    const { data } = await prepareAcceptOfferMutation({
      variables: {
        input: {
          offerId,
          settlementInfo,
          attemptReference,
          signedAmount,
        },
      },
    });

    const authorizations = data?.prepareAcceptOffer?.authorizations || [];

    const signedAuthorizations =
      (signAuthorizations &&
        (await getAuthorizationApprovals(authorizations))) ||
      undefined;

    return {
      settlementInfo,
      authorizations,
      approvals: signedAuthorizations?.authorizationApprovals,
    };
  };

  return {
    prepareAcceptOffer,
    loading,
  };
};

export default usePrepareAcceptOffer;
